import { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import LegalModal from "@/components/LegalModal";
import { legalContent } from "@/utils/legalContent";
import { partnerAcceptanceCopy, partnerContractDocument } from "@/utils/partnerLegalContent";

type Props = {
  contractAccepted: boolean;
  lgpdAccepted: boolean;
  marketingAccepted: boolean;
  onContractAcceptedChange: (value: boolean) => void;
  onLgpdAcceptedChange: (value: boolean) => void;
  onMarketingAcceptedChange: (value: boolean) => void;
  disabled?: boolean;
  contractError?: string;
  lgpdError?: string;
  marketingError?: string;
  showPromotionHighlight?: boolean;
};

const PartnerLegalAcceptance = ({
  contractAccepted,
  lgpdAccepted,
  marketingAccepted,
  onContractAcceptedChange,
  onLgpdAcceptedChange,
  onMarketingAcceptedChange,
  disabled = false,
  contractError,
  lgpdError,
  marketingError,
  showPromotionHighlight = false,
}: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [contractUnlocked, setContractUnlocked] = useState(false);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollTop = 0;
    setContractUnlocked(false);
    onContractAcceptedChange(false);
    // O fluxo sempre reabre na versão atual com o contrato desmarcado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const helperText = useMemo(() => {
    if (contractUnlocked) {
      return "Leitura concluída. Você já pode marcar o aceite do contrato.";
    }
    return "Role até o final do contrato para liberar o aceite.";
  }, [contractUnlocked]);

  const handleScroll = () => {
    const element = scrollContainerRef.current;
    if (!element || contractUnlocked) return;
    const threshold = 16;
    const reachedEnd = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;
    if (reachedEnd) {
      setContractUnlocked(true);
    }
  };

  return (
    <div className="space-y-5 rounded-[24px] border border-[#E7E2D8] bg-[#F7FBF8] p-5">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#215834]/70">Contrato de parceria</p>
            <h4 className="font-fraunces text-2xl text-[#215834]">{partnerContractDocument.title}</h4>
          </div>
          <span className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-[#215834] ring-1 ring-[#215834]/10">
            Versão {partnerContractDocument.versionLabel}
          </span>
        </div>
        <p className="font-author text-sm text-[#52605C]">
          O contrato completo precisa ser exibido antes do aceite e o checkbox só libera depois da rolagem final.
        </p>
        {showPromotionHighlight ? (
          <div className="rounded-2xl border border-[#D9B8AE] bg-white px-4 py-3 text-sm font-medium text-[#215834]">
            {partnerAcceptanceCopy.promotionHighlight}
          </div>
        ) : null}
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="max-h-80 overflow-y-auto rounded-[22px] border border-[#DCE7E0] bg-white px-5 py-4 shadow-inner"
      >
        <div className="whitespace-pre-wrap font-author text-sm leading-7 text-[#2C3E37]">
          {partnerContractDocument.displayContent}
        </div>
      </div>

      <p className={`text-xs font-medium ${contractUnlocked ? "text-[#215834]" : "text-[#A65C48]"}`}>{helperText}</p>

      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={contractAccepted}
            disabled={disabled || !contractUnlocked}
            onCheckedChange={(value) => onContractAcceptedChange(Boolean(value))}
            className="mt-0.5 h-6 w-6 rounded-full border-2 border-[#215834] data-[state=checked]:bg-[#215834]"
          />
          <div className="space-y-1 text-sm font-author text-[#52605C]">
            <p>{partnerAcceptanceCopy.contractCheckbox}</p>
            {!contractUnlocked ? <p className="text-xs text-[#A65C48]">O aceite é liberado ao final da rolagem.</p> : null}
            {contractError ? <p className="text-xs text-red-600">{contractError}</p> : null}
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            checked={lgpdAccepted}
            disabled={disabled}
            onCheckedChange={(value) => onLgpdAcceptedChange(Boolean(value))}
            className="mt-0.5 h-6 w-6 rounded-full border-2 border-[#215834] data-[state=checked]:bg-[#215834]"
          />
          <div className="space-y-1 text-sm font-author text-[#52605C]">
            <p>
              Autorizo o tratamento dos meus dados pessoais para as finalidades de cadastro, intermediação de reservas e pagamentos,
              conforme a{" "}
              <LegalModal
                triggerText="Política de Privacidade"
                title={legalContent.politicaPrivacidade.title}
                content={legalContent.politicaPrivacidade.content}
                className="inline bg-transparent p-0 font-normal text-[#215834] underline hover:text-[#184327]"
              />{" "}
              , nos termos da Lei nº 13.709/2018 (LGPD).
            </p>
            {lgpdError ? <p className="text-xs text-red-600">{lgpdError}</p> : null}
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            checked={marketingAccepted}
            disabled={disabled}
            onCheckedChange={(value) => onMarketingAcceptedChange(Boolean(value))}
            className="mt-0.5 h-6 w-6 rounded-full border-2 border-[#215834] data-[state=checked]:bg-[#215834]"
          />
          <div className="space-y-1 text-sm font-author text-[#52605C]">
            <p>{partnerAcceptanceCopy.marketingCheckbox}</p>
            <p className="text-xs text-[#66746F]">Opcional. Vem desmarcado por padrão.</p>
            {marketingError ? <p className="text-xs text-red-600">{marketingError}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerLegalAcceptance;
